import React from "react";
import keyBy from "lodash/keyBy";
import times from "lodash/times";
import moment from "moment";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import SvgIcon from "../../../parts/SvgIcon";

const {
  DATE_FORMATS: { DATE_YEAR_FORMAT, DATE_DAY_FORMAT },
} = require("../../../constants");

const getDailyStatsByDate = (dailyStats) => {
  return keyBy(dailyStats, (e) =>
    moment
      .utc((e.utcDate && e.utcDate.toString()) || e.date)
      .format(DATE_DAY_FORMAT)
  );
};

class CalendarWeekComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      weeks: [props.activeWeek],
      addedWeekId: null,
      dailyStatsByDate: getDailyStatsByDate(props.dailyStats),
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const { query, activeWeek, activeUser, dailyStats } = this.props;
    const { addedWeekId } = this.state;
    const weeks = this.state.weeks.slice();
    const weekDay = activeUser.startingCalendarDay || 0;

    if (!activeWeek.isSame(prevProps.activeWeek)) {
      this.setState({
        weeks: [activeWeek],
        dailyStatsByDate: getDailyStatsByDate(dailyStats),
      });
    }
    if (query.start && query.start !== prevProps.query.start) {
      const start = moment(query.start, DATE_DAY_FORMAT)
        .startOf("week")
        .weekday(weekDay);
      const addedWeekId = start.format("YYYYMMDD");
      weeks.unshift(start);

      this.setState({
        weeks,
        addedWeekId,
        dailyStatsByDate: getDailyStatsByDate(dailyStats),
      });
    }
    if (query.end && query.end !== prevProps.query.end) {
      const end = moment(query.end, DATE_DAY_FORMAT).subtract(6, "days");
      const addedWeekId = end.format("YYYYMMDD");
      weeks.push(end);

      this.setState({
        weeks,
        addedWeekId,
        dailyStatsByDate: getDailyStatsByDate(dailyStats),
      });
    }
    if (addedWeekId && addedWeekId !== prevState.addedWeekId) {
      const elementToScroll = document.getElementById(addedWeekId);
      if (elementToScroll) {
        elementToScroll.scrollIntoView({ behavior: "smooth" });
      }
    }
  }

  renderAddWeek(type = "subsequent", idx) {
    const { weeks } = this.state;
    const lastIdx = weeks.length - 1;

    if (type === "previous") {
      return (
        <a
          href={`/actions/refresh?start=${weeks[0]
            .clone()
            .subtract(1, "weeks")
            .format(DATE_DAY_FORMAT)}`}
          style={{ visibility: idx === 0 ? "visible" : "hidden" }}
          className="add-week"
        >
          <button data-button-type="tertiary" data-icon-only="">
            <SvgIcon src="/img/new-icons/arrow-square-left.svg" />
          </button>
          <span>Show previous week</span>
        </a>
      );
    }

    return (
      <a
        href={`/actions/refresh?end=${weeks[lastIdx]
          .clone()
          .add(13, "days")
          .format(DATE_DAY_FORMAT)}`}
        style={{ visibility: idx === lastIdx ? "visible" : "hidden" }}
        className="add-week"
      >
        <span>Show subsequent week</span>
        <button data-button-type="tertiary" data-icon-only="">
          <SvgIcon src="/img/new-icons/arrow-square-right.svg" />
        </button>
      </a>
    );
  }

  render() {
    const {
      activeWeek,
      endOfWeek,
      query,
      workoutNotes,
      workoutCoachNotes,
      WeekDay,
    } = this.props;
    const { weeks, dailyStatsByDate } = this.state;
    const hasMultipleWeeks = weeks.length > 1;
    const conditionalStyle = hasMultipleWeeks ? { visibility: "hidden" } : {};
    const conditionalClass = hasMultipleWeeks ? " multiple-weeks" : "";

    return (
      <main className="Calendar">
        <header className={`${conditionalClass}`}>
          <section>
            <hgroup>
              <a
                href={`/actions/refresh?week=${moment().format(
                  DATE_DAY_FORMAT
                )}`}
                style={conditionalStyle}
                data-button-type="outline"
                role="button"
              >
                This Week
              </a>
            </hgroup>

            <hgroup>
              <div role="month-handler" style={conditionalStyle}>
                <div className="!bg-[--dark-grey]">
                  <a
                    data-button-type="text-white"
                    role="button"
                    data-icon-only
                    href={`/actions/refresh?week=${activeWeek
                      .clone()
                      .add(-1, "weeks")
                      .format(DATE_DAY_FORMAT)}`}
                  >
                    <SvgIcon src="/img/new-icons/arrow-left.svg" />
                  </a>
                  <span className="flex-shrink-0">
                    {activeWeek.format("MMM D")} -{" "}
                    {endOfWeek.format("MMM D, YYYY")}
                  </span>
                  <a
                    data-button-type="text-white"
                    role="button"
                    data-icon-only
                    href={`/actions/refresh?week=${activeWeek
                      .clone()
                      .add(1, "weeks")
                      .format(DATE_DAY_FORMAT)}`}
                  >
                    <SvgIcon src="/img/new-icons/arrow-right.svg" />
                  </a>
                </div>
              </div>
            </hgroup>

            <hgroup>
              <ToggleLinksInput
                name="view"
                options={[
                  {
                    text: "Month",
                    value: "month",
                    href: `/actions/refresh?view=month&month=${
                      query.week
                        ? moment(query.week, DATE_DAY_FORMAT).format(
                            DATE_YEAR_FORMAT
                          )
                        : query.date
                        ? moment(query.date, DATE_DAY_FORMAT).format(
                            DATE_YEAR_FORMAT
                          )
                        : ""
                    }&week=&date=&start=&end=`,
                  },
                  {
                    text: "Week",
                    value: "week",
                    href: `/actions/refresh?view=week&week=${
                      query.month
                        ? moment(query.month, DATE_YEAR_FORMAT).format(
                            DATE_DAY_FORMAT
                          )
                        : query.date
                        ? query.date
                        : ""
                    }&month=&date=&start=&end=`,
                  },
                  {
                    text: "Day",
                    value: "day",
                    href: `/actions/refresh?view=day&date=${
                      query.month
                        ? moment(query.month, DATE_YEAR_FORMAT).format(
                            DATE_DAY_FORMAT
                          )
                        : query.week
                        ? query.week
                        : ""
                    }&month=&week=&start=&end=`,
                  },
                ]}
              />
            </hgroup>
          </section>
        </header>

        <section className={`Calendar-Weeks${conditionalClass}`}>
          {weeks.map((week, idx) => {
            return (
              <article
                className="Calendar-Week"
                id={week.format("YYYYMMDD")}
                key={week.format("YYYYMMDD")}
              >
                <header>
                  {this.renderAddWeek("previous", idx)}
                  {hasMultipleWeeks && (
                    <p className="s1">
                      {week.format("MMM D")} -{" "}
                      {week.clone().add(6, "days").format("MMM D, YYYY")}
                    </p>
                  )}
                  {this.renderAddWeek("subsequent", idx)}
                </header>
                {times(7, (day) => {
                  const date = week.clone().add(day, "days");
                  let notes, coachNotes;
                  const dailyStats =
                    dailyStatsByDate[date.format(DATE_DAY_FORMAT)];

                  if (workoutNotes.notes && workoutNotes.notes.length) {
                    notes = workoutNotes.notes.find((note) => {
                      if (
                        date.format("YYYY-MM-DD") ===
                        moment.utc(note.date).format("YYYY-MM-DD")
                      ) {
                        return true;
                      }
                      return false;
                    });
                  }
                  if (
                    workoutCoachNotes &&
                    workoutCoachNotes.notes &&
                    workoutCoachNotes.notes.length
                  ) {
                    coachNotes = workoutCoachNotes.notes.find((note) => {
                      if (
                        date.format("YYYY-MM-DD") ===
                        moment.utc(note.date).format("YYYY-MM-DD")
                      ) {
                        return true;
                      }
                      return false;
                    });
                  }
                  return (
                    <WeekDay
                      key={`${idx}${day}`}
                      date={date}
                      notes={notes}
                      coachNotes={coachNotes}
                      dailyStats={dailyStats}
                    />
                  );
                })}
              </article>
            );
          })}
        </section>
      </main>
    );
  }
}

export default CalendarWeekComponent;
